// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { useResizeObserver } from '../../hooks';
export const useWidths = _ref => {
  let {
    tabNavRef,
    tabListRef,
    addonRef,
    baseClassName,
    monospaced = false,
    childrenCount
  } = _ref;
  const [compact, setCompact] = useState(false);
  const [monospacedWidth, setMonospacedWidth] = useState(undefined);
  const [visibleLength, setVisibleLength] = useState(childrenCount);
  const lastCollapsedKey = useRef('');
  const recalculateWidths = useCallback(() => {
    if (!tabNavRef.current || !tabListRef.current) {
      return;
    }
    const fakeTabElements = tabNavRef.current.querySelectorAll(`.${baseClassName}__tab-list--fake .${baseClassName}__tab`);
    const moreElement = fakeTabElements[fakeTabElements.length - 1];
    const tabLength = fakeTabElements.length - 1;
    const fakeTabWidths = Array.from(fakeTabElements).slice(0, -1).map(el => el.getBoundingClientRect().width);
    let tabWidths = fakeTabWidths;
    let monospacedWidth = undefined;
    if (monospaced) {
      const linkElements = tabNavRef.current.querySelectorAll(`.${baseClassName}__tab-list--fake .${baseClassName}__link`);
      const linkWidths = Array.from(linkElements).slice(0, -1).map(el => el.getBoundingClientRect().width);
      monospacedWidth = linkWidths.length ? Math.max(...linkWidths) : undefined;
      tabWidths = fakeTabWidths.map((width, index) => (monospacedWidth ?? 0) + width - linkWidths[index]);
    }
    setMonospacedWidth(monospacedWidth);
    const tabsWidth = tabWidths.reduce((acc, width) => acc + width, 0);
    const tabNavWidth = tabNavRef.current.getBoundingClientRect().width;
    const addon = tabNavRef.current.querySelector(`.${baseClassName}__addon`) ?? tabNavRef.current.querySelector(`.${baseClassName}__search`);
    const addonWidth = addon ? addon.getBoundingClientRect().width : 0;
    let availableSpace = tabNavWidth - addonWidth;
    const allTabsFit = availableSpace >= tabsWidth;
    const getContentKey = () => {
      const addonWrapperWidth = addonRef.current?.getBoundingClientRect().width ?? 0;
      return `${fakeTabWidths.join('-')}-${tabNavWidth}-${addonWrapperWidth}-${monospacedWidth}`;
    };
    if (allTabsFit) {
      setVisibleLength(tabLength);
      if (!compact) {
        return;
      }
      if (lastCollapsedKey.current !== getContentKey()) {
        setCompact(false);
      }
      return;
    }
    if (!allTabsFit && !compact) {
      setCompact(true);
      lastCollapsedKey.current = getContentKey();
      return;
    }
    const moreWidth = moreElement ? moreElement.getBoundingClientRect().width : 0;
    availableSpace -= moreWidth;
    const calculateVisibleLength = () => {
      let totalWidth = 0;
      for (let i = 0; i < tabWidths.length; i++) {
        totalWidth += tabWidths[i];
        if (totalWidth > availableSpace) {
          return i;
        }
      }
      return 0;
    };
    setVisibleLength(calculateVisibleLength());
  }, [tabNavRef, tabListRef, addonRef, baseClassName, monospaced, compact]);
  useLayoutEffect(() => {
    recalculateWidths();
  }, [recalculateWidths]);
  useResizeObserver({
    ref: tabNavRef,
    onResize: recalculateWidths
  });
  useResizeObserver({
    ref: tabListRef,
    onResize: recalculateWidths
  });
  useResizeObserver({
    ref: addonRef,
    onResize: recalculateWidths
  });
  return {
    recalculateWidths,
    monospacedWidth,
    compact,
    visibleLength
  };
};